import { useId, useState } from 'react';
import { TRACKER_NAMES, type TrackerId } from '../api/types';
import { finite } from './charts';

/* Graphics for comparing the trackers themselves. Same material as the glass
   kit (ui/glass.tsx): frosted beds, solid marks in their identity colour with
   a gloss that only lightens. A tracker's colour always means that tracker;
   agreement is drawn on the blue ordinal ramp, never green against red. */

type Fmt = (v: number | null | undefined) => string;

/* ---------------------------------------------------------------- emblem */

const GLYPH: Record<TrackerId, string> = { circus: 'c', fairgambling: 'FG', tanzanite: 'T', stakesim: 'S' };

/** A tracker's mark: a glass tile in its colour with its initial. */
export function Emblem({ id, size = 44 }: { id: TrackerId; size?: number }) {
  const uid = useId().replace(/:/g, '');
  const color = `var(--tracker-${id})`;
  const g = GLYPH[id];
  return (
    <svg className="emblem" width={size} height={size} viewBox="0 0 44 44" aria-hidden>
      <defs>
        <linearGradient id={`${uid}-f`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" style={{ stopColor: `color-mix(in srgb, ${color} 72%, #fff)` }} />
          <stop offset="0.6" style={{ stopColor: color }} />
        </linearGradient>
        <linearGradient id={`${uid}-s`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity="0.55" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="42" height="42" rx="13" fill={`url(#${uid}-f)`} />
      <rect x="1" y="1" width="42" height="42" rx="13" fill={`url(#${uid}-s)`} />
      <rect x="1.5" y="1.5" width="41" height="41" rx="12.5" fill="none" stroke="#fff" strokeOpacity="0.55" />
      <text x="22" y="22" dy="0.36em" textAnchor="middle" className="emblem-glyph" style={{ fontSize: g.length > 1 ? 15 : 20 }}>{g}</text>
    </svg>
  );
}

/* ------------------------------------------------------- deviation swarm */

export interface SwarmRow { id: TrackerId; counted: boolean; summary: string; points: { key: string; name: string; dev: number }[] }

// A signed log scale: a tracker 5% out and one 150% out both need to be
// readable on the same axis, and nearly every casino sits within 20%.
const slog = (d: number) => Math.sign(d) * Math.log10(1 + Math.abs(d) / 5);
const TICKS = [-80, -50, -20, -5, 0, 5, 20, 50, 100, 200];

function quantile(sorted: number[], q: number): number {
  if (!sorted.length) return 0;
  const i = (sorted.length - 1) * q;
  const lo = Math.floor(i), hi = Math.ceil(i);
  return (sorted[lo] as number) + ((sorted[hi] as number) - (sorted[lo] as number)) * (i - lo);
}

/** Every casino as a dot at its distance from the median, one row per tracker, with the middle half as a glass capsule. */
export function DevSwarm({ rows }: { rows: SwarmRow[] }) {
  const [hover, setHover] = useState<string | null>(null);
  const W = 900, L = 214, R = 16, ROW = 74, T = 10, B = 26;
  const H = T + rows.length * ROW + B;
  const lo = slog(-90), hi = slog(220);
  const x = (d: number) => L + ((slog(Math.max(-90, Math.min(220, d))) - lo) / (hi - lo)) * (W - L - R);
  const r = 4.2;

  return (
    <div className="swarm">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Each casino's distance from the median, per tracker">
        {TICKS.map((t) => (
          <g key={t}>
            <line x1={x(t)} x2={x(t)} y1={T} y2={H - B} className={t === 0 ? 'swarm-zero' : 'swarm-grid'} />
            <text x={x(t)} y={H - 8} textAnchor="middle" className="swarm-tick">{t > 0 ? `+${t}%` : `${t}%`}</text>
          </g>
        ))}
        {rows.map((row, ri) => {
          const cy = T + ri * ROW + ROW / 2;
          const devs = row.points.map((p) => p.dev).sort((a, b) => a - b);
          const q1 = quantile(devs, 0.25), q3 = quantile(devs, 0.75), med = quantile(devs, 0.5);
          // Beeswarm: each dot takes the nearest free slot above or below the row's centre line.
          const placed: { x: number; y: number }[] = [];
          const dots = [...row.points].sort((a, b) => a.dev - b.dev).map((p) => {
            const px = x(p.dev);
            let y = cy;
            for (let k = 0; k < 14; k++) {
              const off = (k % 2 ? 1 : -1) * Math.ceil(k / 2) * (r * 2 + 0.6);
              const cand = cy + off;
              if (Math.abs(off) > ROW / 2 - r - 2) break;
              if (placed.every((q) => (q.x - px) ** 2 + (q.y - cand) ** 2 >= (r * 2 + 0.4) ** 2)) { y = cand; break; }
            }
            placed.push({ x: px, y });
            return { ...p, px, py: y };
          });
          return (
            <g key={row.id} className={row.counted ? '' : 'swarm-ref'}>
              <text x={0} y={cy - 4} className="swarm-name" style={{ fill: `var(--tracker-${row.id})` }}>{TRACKER_NAMES[row.id]}{row.counted ? '' : ' *'}</text>
              <text x={0} y={cy + 14} className="swarm-sum">{row.summary}</text>
              {devs.length > 1 && <rect x={x(q1)} y={cy - 15} width={Math.max(4, x(q3) - x(q1))} height={30} rx={15} className="swarm-iqr" style={{ fill: `var(--tracker-${row.id})` }} />}
              {dots.map((d) => (
                <circle key={d.key} cx={d.px} cy={d.py} r={hover === d.key ? r + 2 : r} className={`swarm-dot${hover && hover !== d.key ? ' dim' : ''}`}
                  style={{ fill: `var(--tracker-${row.id})` }} onMouseEnter={() => setHover(d.key)} onMouseLeave={() => setHover(null)}>
                  <title>{`${d.name}: ${TRACKER_NAMES[row.id]} ${d.dev >= 0 ? '+' : ''}${d.dev.toFixed(1)}% vs the median`}</title>
                </circle>
              ))}
              {devs.length > 0 && <line x1={x(med)} x2={x(med)} y1={cy - 20} y2={cy + 20} className="swarm-med" style={{ stroke: `var(--tracker-${row.id})` }} />}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------ agreement matrix */

export interface PairStat { shared: number; within: number; ratio: number | null }

/** Share of shared casinos where two trackers are within 10%, for every pair, on the blue ramp. */
export function AgreeMatrix({ ids, stat, listed, href }: {
  ids: TrackerId[]; stat: (a: TrackerId, b: TrackerId) => PairStat; listed: (id: TrackerId) => number; href: (a: TrackerId, b: TrackerId) => string;
}) {
  const step = (p: number) => (p < 20 ? 0 : p < 40 ? 1 : p < 60 ? 2 : p < 80 ? 3 : 4);
  return (
    <div className="agree" style={{ gridTemplateColumns: `minmax(92px, auto) repeat(${ids.length}, minmax(0, 1fr))` }}>
      <span />
      {ids.map((id) => <span key={id} className="agree-col"><i className="gdot" style={{ background: `var(--tracker-${id})` }} aria-hidden />{TRACKER_NAMES[id]}</span>)}
      {ids.map((a) => (
        <div key={a} className="agree-row">
          <span className="agree-rowhead"><i className="gdot" style={{ background: `var(--tracker-${a})` }} aria-hidden />{TRACKER_NAMES[a]}</span>
          {ids.map((b) => {
            if (a === b) return <span key={b} className="agree-cell self"><b>{listed(a)}</b><small>casinos listed</small></span>;
            const s = stat(a, b);
            if (!s.shared) return <span key={b} className="agree-cell none"><small>no casinos in common</small></span>;
            const p = (s.within / s.shared) * 100;
            return (
              <a key={b} className={`agree-cell a${step(p)}`} href={href(a, b)} title={`${TRACKER_NAMES[a]} and ${TRACKER_NAMES[b]}: ${s.within} of ${s.shared} shared casinos within 10%`}>
                <b>{Math.round(p)}%</b><small>of {s.shared} within 10%</small>
              </a>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------- who lists what */

/** Casinos grouped by exactly which trackers list them: a bar per combination, dots for its members. */
export function Overlap({ ids, sets }: { ids: TrackerId[]; sets: TrackerId[][] }) {
  const tally = new Map<string, { members: TrackerId[]; n: number }>();
  for (const s of sets) {
    const members = ids.filter((id) => s.includes(id));
    if (!members.length) continue;
    const k = members.join('+');
    tally.set(k, { members, n: (tally.get(k)?.n ?? 0) + 1 });
  }
  const combos = [...tally.values()].sort((a, b) => b.n - a.n || b.members.length - a.members.length).slice(0, 10);
  const top = Math.max(1, ...combos.map((c) => c.n));
  const DOT = 26;

  return (
    <div className="overlap" style={{ gridTemplateColumns: `minmax(96px, auto) repeat(${combos.length}, minmax(0, 1fr))` }}>
      <span className="overlap-axis">casinos</span>
      {combos.map((c) => {
        const solo = c.members.length === 1 ? c.members[0] : undefined;
        return (
          <div key={c.members.join('+')} className="overlap-bar" title={`${c.n} casinos listed by ${c.members.length === ids.length ? 'every tracker' : c.members.map((m) => TRACKER_NAMES[m]).join(' + ')}${solo ? ' only' : ''}`}>
            <b>{c.n}</b>
            <span className="gcol-bar"><i style={{ height: `${(c.n / top) * 100}%`, ['--gb-color' as string]: solo ? `var(--tracker-${solo})` : 'var(--series-1)' }} /></span>
          </div>
        );
      })}
      <div className="overlap-names">
        {ids.map((id) => <span key={id} style={{ height: DOT }}><i className="gdot" style={{ background: `var(--tracker-${id})` }} aria-hidden />{TRACKER_NAMES[id]}</span>)}
      </div>
      {combos.map((c) => {
        const idx = c.members.map((m) => ids.indexOf(m));
        const first = Math.min(...idx), last = Math.max(...idx);
        return (
          <div key={c.members.join('+')} className="overlap-dots">
            {last > first && <i className="overlap-link" style={{ top: first * DOT + DOT / 2, height: (last - first) * DOT }} />}
            {ids.map((id) => (
              <span key={id} style={{ height: DOT }}>
                <i className={c.members.includes(id) ? 'on' : ''} style={c.members.includes(id) ? { background: `var(--tracker-${id})` } : undefined} />
              </span>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/* ----------------------------------------------------------- bump chart */

export interface BumpLine { key: string; name: string; ranks: Partial<Record<TrackerId, number>> }

const BUMP_COLORS = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)', 'var(--series-4)', 'var(--series-5)', 'var(--series-6)', 'var(--series-7)', 'var(--series-8)'];

/** The same casinos in each tracker's order: a line per casino, a column per tracker. */
export function Bump({ ids, lines }: { ids: TrackerId[]; lines: BumpLine[] }) {
  const [hover, setHover] = useState<string | null>(null);
  const maxRank = Math.max(1, ...lines.flatMap((l) => ids.map((id) => l.ranks[id] ?? 0)));
  const W = 760, L = 150, R = 150, T = 34, ROW = 34;
  const H = T + (maxRank - 1) * ROW + 24;
  const cx = (i: number) => L + (ids.length > 1 ? (i / (ids.length - 1)) * (W - L - R) : (W - L - R) / 2);
  const cy = (rank: number) => T + (rank - 1) * ROW;

  return (
    <div className="bump">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Rank of each casino on each tracker">
        {ids.map((id, i) => (
          <g key={id}>
            <line x1={cx(i)} x2={cx(i)} y1={T - 12} y2={H - 16} className="bump-col" />
            <text x={cx(i)} y={12} textAnchor="middle" className="bump-head" style={{ fill: `var(--tracker-${id})` }}>{TRACKER_NAMES[id]}</text>
          </g>
        ))}
        {lines.map((l, li) => {
          const color = BUMP_COLORS[li % BUMP_COLORS.length] as string;
          const pts = ids.map((id, i) => ({ id, i, rank: l.ranks[id] })).filter((p): p is { id: TrackerId; i: number; rank: number } => finite(p.rank));
          if (!pts.length) return null;
          let d = `M${cx((pts[0] as { i: number }).i)},${cy((pts[0] as { rank: number }).rank)}`;
          for (let k = 1; k < pts.length; k++) {
            const a = pts[k - 1] as { i: number; rank: number }, b = pts[k] as { i: number; rank: number };
            const mx = (cx(a.i) + cx(b.i)) / 2;
            d += ` C${mx},${cy(a.rank)} ${mx},${cy(b.rank)} ${cx(b.i)},${cy(b.rank)}`;
          }
          const on = !hover || hover === l.key;
          const first = pts[0] as { i: number; rank: number }, last = pts[pts.length - 1] as { i: number; rank: number };
          return (
            <g key={l.key} className={`bump-line${on ? '' : ' dim'}`} onMouseEnter={() => setHover(l.key)} onMouseLeave={() => setHover(null)}>
              <path d={d} className="bump-path" style={{ stroke: color }} />
              <path d={d} className="bump-hit" />
              {pts.map((p) => (
                <g key={p.id}>
                  <circle cx={cx(p.i)} cy={cy(p.rank)} r={11} className="bump-node" style={{ stroke: color }} />
                  <text x={cx(p.i)} y={cy(p.rank)} dy="0.35em" textAnchor="middle" className="bump-rank">{p.rank}</text>
                </g>
              ))}
              <text x={cx(first.i) - 20} y={cy(first.rank)} dy="0.35em" textAnchor="end" className="bump-name">{l.name}</text>
              <text x={cx(last.i) + 20} y={cy(last.rank)} dy="0.35em" className="bump-name">{l.name}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* -------------------------------------------------------------- scatter */

export interface ScatterPoint { key: string; name: string; x: number; y: number; href?: string }

/** Two trackers' figures for the same casinos, log on both axes, with the within-10% band along the diagonal. */
export function Scatter({ points, xName, yName, xColor, yColor, format }: {
  points: ScatterPoint[]; xName: string; yName: string; xColor: string; yColor: string; format: Fmt;
}) {
  const [hover, setHover] = useState<ScatterPoint | null>(null);
  const live = points.filter((p) => p.x > 0 && p.y > 0);
  if (live.length < 2) return <p className="empty">Fewer than two casinos in common.</p>;
  const W = 640, H = 420, L = 64, R = 18, T = 16, B = 48;
  const all = live.flatMap((p) => [p.x, p.y]);
  const e0 = Math.floor(Math.log10(Math.min(...all))), e1 = Math.ceil(Math.log10(Math.max(...all)));
  const sx = (v: number) => L + ((Math.log10(v) - e0) / (e1 - e0 || 1)) * (W - L - R);
  const sy = (v: number) => H - B - ((Math.log10(v) - e0) / (e1 - e0 || 1)) * (H - T - B);
  const decades = Array.from({ length: e1 - e0 + 1 }, (_, i) => 10 ** (e0 + i));
  const lo = 10 ** e0, hi = 10 ** e1;
  // The ±10% band: y between x/1.1 and x·1.1, drawn as a polygon along the diagonal.
  const band = `${sx(lo)},${sy(lo * 1.1)} ${sx(hi / 1.1)},${sy(hi)} ${sx(hi)},${sy(hi)} ${sx(hi)},${sy(hi / 1.1)} ${sx(lo * 1.1)},${sy(lo)} ${sx(lo)},${sy(lo)}`;
  const tone = (p: ScatterPoint) => { const d = Math.abs(Math.log(p.y / p.x)); return d <= Math.log(1.1) ? 0 : d <= Math.log(1.25) ? 1 : d <= Math.log(2) ? 2 : 3; };

  return (
    <div className="scatter">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${yName} against ${xName}, one dot per casino both list`}>
        {decades.map((v) => (
          <g key={v}>
            <line x1={sx(v)} x2={sx(v)} y1={T} y2={H - B} className="ggrid" />
            <line x1={L} x2={W - R} y1={sy(v)} y2={sy(v)} className="ggrid" />
            <text x={sx(v)} y={H - B + 18} textAnchor="middle" className="axis">{format(v)}</text>
            <text x={L - 8} y={sy(v)} dy="0.35em" textAnchor="end" className="axis">{format(v)}</text>
          </g>
        ))}
        <polygon points={band} className="scatter-band" />
        <line x1={sx(lo)} y1={sy(lo)} x2={sx(hi)} y2={sy(hi)} className="scatter-diag" />
        {/* Along the diagonal, low on it: the dots crowd the top end. */}
        {(() => {
          const at = 10 ** (e0 + (e1 - e0) * 0.2);
          const deg = (Math.atan2(sy(hi) - sy(lo), sx(hi) - sx(lo)) * 180) / Math.PI;
          return <text transform={`translate(${sx(at)} ${sy(at)}) rotate(${deg}) translate(0 -7)`} className="scatter-note">same figure on both</text>;
        })()}
        <text x={(L + W - R) / 2} y={H - 6} textAnchor="middle" className="axis-title" style={{ fill: xColor }}>{xName} →</text>
        <text transform={`translate(14 ${(T + H - B) / 2}) rotate(-90)`} textAnchor="middle" className="axis-title" style={{ fill: yColor }}>{yName} →</text>
        {live.map((p) => {
          const dot = (
            <circle cx={sx(p.x)} cy={sy(p.y)} r={hover?.key === p.key ? 8 : 6} className={`scatter-dot t${tone(p)}`}
              onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)} />
          );
          return p.href ? <a key={p.key} href={p.href} aria-label={p.name}>{dot}</a> : <g key={p.key}>{dot}</g>;
        })}
      </svg>
      {hover && (
        <div className="gtip gtip-card" style={{ left: `${(sx(hover.x) / W) * 100}%`, top: `calc(${(sy(hover.y) / H) * 100}% - 96px)` }}>
          <small>{hover.name}</small>
          <div><span><i className="gdot" style={{ background: xColor }} aria-hidden />{xName}</span><b>{format(hover.x)}</b></div>
          <div><span><i className="gdot" style={{ background: yColor }} aria-hidden />{yName}</span><b>{format(hover.y)}</b></div>
        </div>
      )}
    </div>
  );
}
